import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Button, Grid, IconButton, useMediaQuery, Box } from "@mui/material";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import EditIcon from "@mui/icons-material/Edit";
import moment from "moment";
import { useFormik } from "formik";
import PropTypes from "prop-types";

import Backdrop from "Components/Common/Backdrop";
import BreadCrumb from "Components/Common/BreadCrumb";
import BaseTable from "Components/Common/BaseTable";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import Tooltip from "Components/Common/Tooltip";
import {
  billingSubscriptionTypes,
  getOptionLabel,
  getOptionValue,
} from "common/enums";
import EditPowerStatusModal from "./EditPowerStatusModal";
import { SUPER_ADMIN, powerAdmin3Roles } from "common/constant";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";

import { getImageUrl, isAuthorizedUser } from "utils";
import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useLazyUnpaidBillsQuery } from "apis/Billing";
import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";

const UnpaidBills = ({ t }) => {
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [documentToPreview, setDocumentToPreview] = useState("");
  const [payloadValues, setPayloadValues] = useState(null);

  const { roles } = useSelector((state) => state.Login);

  const resetPageRef = useRef();

  const [
    getUnpaidBills,
    { data: { data: unPaidBills } = {}, isLoading, isFetching },
  ] = useLazyUnpaidBillsQuery();

  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const md = useMediaQuery("(min-width: 600px) and (max-width: 899px)");
  const sm = useMediaQuery("(max-width: 599px)");

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter((option) => subscriptionType === option.subscriptionType)
            .map(({ billDateDuration, id, status }) => ({
              label: billDateDuration,
              value: id,
              status,
            }))
        : [],
    [billingCycleOptions, subscriptionType]
  );

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      billCycleId:
        updatedBillingCycleOptions.find(
          ({ status }) => status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "",
      consumerNumber: "",
      meterNumber: "",
    },
    onSubmit: async ({ billCycleId, consumerNumber, meterNumber }) => {
      try {
        if (billCycleId || consumerNumber || meterNumber) {
          await getUnpaidBills({
            billingCycleId: billCycleId,
            consumerNumber,
            meterNumber,
            subscriptionType,
          });
          handleResetPage();
        }
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    setFieldValue,
    values: { billCycleId },
  } = formik;

  useEffect(() => {
    (async () => {
      try {
        if (subscriptionType && updatedBillingCycleOptions.length) {
          await getUnpaidBills({
            subscriptionType,
            billingCycleId:
              updatedBillingCycleOptions.find(
                ({ status }) =>
                  status !== getOptionValue("billingStatus", "Closed")
              )?.value ??
              updatedBillingCycleOptions.at(-1)?.value ??
              "",
          });
        }
      } catch (error) {}
    })();
  }, [subscriptionType, updatedBillingCycleOptions]);

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length) {
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  const columns = useMemo(
    () => [
      { title: "Bill Cycle", field: "billCycle" },
      { title: "Consumer Number", field: "consumerNumber" },
      { title: "Consumer Name", field: "consumerName" },
      { title: "Net Payable", field: "netPayable" },
      { title: "Pending Dues", field: "pendingDues" },
      {
        title: "Status",
        renderCell: ({ status }) =>
          getOptionLabel("billCalculationStatus", status),
      },
      {
        title: "Consumer Status",
        renderCell: ({ utilityConsumerStatus }) =>
          getOptionLabel("utilityConsumerStatusEnum", utilityConsumerStatus),
      },
      {
        title: "Payment Due Date",
        renderCell: ({ paymentDueDate }) =>
          moment(paymentDueDate).isValid()
            ? convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: "Actions",
        renderCell: ({
          url,
          utilityConsumerId,
          id,
          paymentDueDate,
          netPayable,
        }) => (
          <Box minWidth="100px">
            <IconButton
              onClick={() =>
                setDocumentToPreview({
                  url: getImageUrl(url),
                  documentTypeName: "Bill",
                  documentType: "pdf",
                  documentName: "bill",
                })
              }
            >
              <Tooltip title={t("View Bill Receipt")}>
                <ViewIcon
                  fontSize="small"
                  color="primary"
                  className="cursor-pointer"
                />
              </Tooltip>
            </IconButton>
            {isAuthorizedUser(roles, [SUPER_ADMIN, ...powerAdmin3Roles]) && (
              <IconButton
                onClick={() =>
                  setPayloadValues({
                    utilityConsumerId,
                    billCalculationIds: id,
                    paymentDueDate,
                    netPayable,
                  })
                }
              >
                <Tooltip title={t("Edit Power status")}>
                  <EditIcon
                    fontSize="small"
                    color="primary"
                    className="cursor-pointer"
                  />
                </Tooltip>
              </IconButton>
            )}
          </Box>
        ),
      },
    ],
    [unPaidBills]
  );

  const clearBtnHandler = async () => {
    try {
      setFieldValue("billCycleId", "");
      setFieldValue("consumerNumber", "");
      setFieldValue("meterNumber", "");
      await getUnpaidBills({ subscriptionType });
      handleResetPage();
    } catch (error) {}
  };

  return (
    <>
      <Backdrop open={isLoading || isFetching} />
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      {!!payloadValues && (
        <EditPowerStatusModal
          payloadValues={payloadValues}
          handleClose={() => setPayloadValues(null)}
          isOtherUtility={
            getOptionValue("billingSubscriptionTypes", "Electricity") !==
            subscriptionType
          }
        />
      )}
      <BreadCrumb title={t("Unpaid Bills")} pageTitle={t("Billing")} />
      <form onSubmit={handleSubmit}>
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Subscription Type") + "*"}
              onChange={(_, { value }) => setSubscriptionType(value)}
              value={
                updatedBillingSubscriptionOptions.find(
                  ({ value }) => value === subscriptionType
                ) ?? ""
              }
              options={updatedBillingSubscriptionOptions}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <SelectField
              label={t("Select bill cycle")}
              name="billCycleId"
              formik={formik}
              options={updatedBillingCycleOptions || []}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              formik={formik}
              placeholder={t("Enter consumer number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              name="meterNumber"
              formik={formik}
              placeholder={t("Enter meter number")}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              className="mb-2 me-2"
              disabled={!billCycleId}
              type="submit"
            >
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={clearBtnHandler}
              disabled={!billCycleId}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
          </Grid>
        </Grid>
      </form>
      <BaseTable
        columns={columns}
        data={unPaidBills}
        pageSize={10}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight={
          sm
            ? "calc(100vh - 350px)"
            : md
            ? "calc(100vh - 300px)"
            : "calc(100vh - 250px)"
        }
      />
    </>
  );
};

UnpaidBills.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(UnpaidBills);
